import {
  kindTokenFallback,
  OPENER_KIND_INFO,
  OPENER_KIND_TOKENS,
  type OpenerColorRole,
  type OpenerKind,
  type ShopifyOpenerThemeDraft,
} from "@loot-os/api-contract";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import type { ReactNode } from "react";

import { OpenerThemeFields } from "./OpenerThemeFields";
import { ColorField } from "./StorefrontFields";

/**
 * The opener theme editor every surface that themes an opener shares (a platform or environment
 * opener style, and a Shopify shop's overlay): every color field on the left, the variables of
 * `kind` first and then the shared theme, and `aside` above `preview` on the right. The preview
 * stays pinned while the fields scroll, so each edit is seen as it is made; the columns are equal
 * so it fits in the window. On a narrow screen the aside and preview come first and scroll with
 * the page. A blank field shows what it falls back to: `fallbackFor` names each role's color,
 * which a kind variable that follows a role shows too.
 */
export function OpenerThemeEditor({
  idPrefix,
  kind,
  draft,
  onChange,
  fallbackFor,
  fontPlaceholder,
  disabled,
  aside,
  preview,
}: {
  idPrefix: string;
  /** The animation whose own variables the editor lists; the spinner has none. */
  kind: OpenerKind;
  draft: ShopifyOpenerThemeDraft;
  onChange: (next: ShopifyOpenerThemeDraft) => void;
  fallbackFor: (role: OpenerColorRole) => string;
  fontPlaceholder: string;
  disabled: boolean;
  /** Above the preview: what the theme belongs to (a style's name and animation, a shop's style). */
  aside: ReactNode;
  preview: ReactNode;
}) {
  const kindTokens = OPENER_KIND_TOKENS[kind];
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="grid content-start gap-6">
        {kindTokens.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle>{OPENER_KIND_INFO[kind].label} colors</CardTitle>
            </CardHeader>
            <CardContent>
              <fieldset className="grid gap-4 sm:grid-cols-2" disabled={disabled}>
                {kindTokens.map((token) => (
                  <ColorField
                    key={token.key}
                    id={`${idPrefix}-kind-${token.key}`}
                    label={token.label}
                    hint={token.key}
                    fallback={kindTokenFallback(token, fallbackFor)}
                    value={draft.kind[token.key] ?? ""}
                    onChange={(value) =>
                      onChange({ ...draft, kind: { ...draft.kind, [token.key]: value } })
                    }
                  />
                ))}
              </fieldset>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardHeader>
            <CardTitle>Theme</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6">
            <OpenerThemeFields
              idPrefix={idPrefix}
              draft={draft}
              onChange={onChange}
              fallbackFor={fallbackFor}
              fontPlaceholder={fontPlaceholder}
              disabled={disabled}
            />
          </CardContent>
        </Card>
      </div>
      {/* The column stretches to the fields' height, which is what the preview pins within. */}
      <div className="order-first grid min-w-0 content-start gap-6 lg:order-none">
        {aside}
        <div className="min-w-0 lg:sticky lg:top-4">{preview}</div>
      </div>
    </div>
  );
}
